'use client';

import { usePathname } from "next/navigation";
import { useState } from "react";
import FontLoader from "@/components/ui/font-loader";
import { Menu, X } from "lucide-react";
import dynamic from 'next/dynamic';

// Dynamically import the StructuredData component to avoid SSR issues
const StructuredData = dynamic(() => import('@/components/seo/structured-data'), { ssr: false });

export default function RootLayoutWrapper({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-ivory/90">
      <FontLoader />
      <StructuredData />
      <header className="fixed top-0 left-0 right-0 z-50 bg-ivory/90 backdrop-blur-sm py-1 shadow-sm">
        <div className="container-custom flex justify-between items-center">
          <a href="/" className="flex items-center -ml-3 -my-2">
            <img
              src="/logos/optimized/micro-leaps-logo.png"
              alt="Micro Leaps Logo"
              className="h-[5rem] md:h-[5.5rem] w-auto"
              width={480}
              height={480}
              style={{
                objectFit: 'contain',
                transform: 'scale(1.5)',
              }}
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex gap-6">
            <a
              href="/what-we-do"
              className={`font-heading hover:text-mid-blue transition-colors relative ${pathname === '/what-we-do'
                ? 'text-mid-blue font-semibold after:absolute after:bottom-[-6px] after:left-0 after:w-full after:h-[2px] after:bg-mid-blue'
                : 'text-oxford'
                }`}
            >
              What We Do
            </a>
            <a
              href="/who-we-are"
              className={`font-heading hover:text-mid-blue transition-colors relative ${pathname === '/who-we-are'
                ? 'text-mid-blue font-semibold after:absolute after:bottom-[-6px] after:left-0 after:w-full after:h-[2px] after:bg-mid-blue'
                : 'text-oxford'
                }`}
            >
              Who We Are
            </a>
            <a
              href="/whats-inspiring-our-work"
              className={`font-heading hover:text-mid-blue transition-colors relative ${pathname === '/whats-inspiring-our-work'
                ? 'text-mid-blue font-semibold after:absolute after:bottom-[-6px] after:left-0 after:w-full after:h-[2px] after:bg-mid-blue'
                : 'text-oxford'
                }`}
            >
              Whats Inspiring Our Work
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-2 md:gap-4">
            <button
              className="md:hidden text-oxford p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>

          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-ivory shadow-lg">
            <nav className="container-custom py-4 flex flex-col">
              <a
                href="/what-we-do"
                className={`font-heading py-3 border-b border-gray-100 ${pathname === '/what-we-do' ? 'text-mid-blue font-semibold' : 'text-oxford'
                  }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                What We Do
              </a>
              <a
                href="/who-we-are"
                className={`font-heading py-3 border-b border-gray-100 ${pathname === '/who-we-are' ? 'text-mid-blue font-semibold' : 'text-oxford'
                  }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Who We Are
              </a>
              <a
                href="/whats-inspiring-our-work"
                className={`font-heading py-3 ${pathname === '/whats-inspiring-our-work' ? 'text-mid-blue font-semibold' : 'text-oxford'
                  }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Whats Inspiring Our Work
              </a>
            </nav>
          </div>
        )}
      </header>
      <main className="pt-[5.5rem]">{children}</main>
      <footer className="bg-oxford text-ivory py-12">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <img
                src="/logos/optimized/micro-leaps-logo.png"
                alt="Micro Leaps Logo"
                className="h-[5rem] md:h-[5.5rem] w-auto brightness-0 invert"
                width={480}
                height={480}
                style={{
                  objectFit: 'contain',
                  transform: 'scale(1.5)',
                }}
              />
              <p className="body-text max-w-md">
                Transforming education to truly empower every child.
              </p>
            </div>
            <div>
              <h3 className="font-heading text-xl mb-4">Contact Us</h3>
              <p className="body-text">Email: prince@seekhshala.org</p>
            </div>
            <div>
              <h3 className="font-heading text-xl mb-4">Learn More</h3>
              <ul className="space-y-2">
                <li><a href="/what-we-do" className="hover:text-mid-blue transition-colors">What We Do</a></li>
                <li><a href="/who-we-are" className="hover:text-mid-blue transition-colors">Who We Are</a></li>
                <li><a href="/whats-inspiring-our-work" className="hover:text-mid-blue transition-colors">Inspiration</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-ivory/20 mt-8 pt-8 text-center">
            <p>&copy; 2025 Micro Leaps. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Mobile Impact Info - Sticky (Informational) */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md shadow-[0_-4px_10px_rgba(0,0,0,0.05)] p-4 z-40 border-t border-gray-100">
        <div className="flex justify-between items-center max-w-lg mx-auto">
          <div>
            <p className="text-sm font-bold text-oxford">Our 2025 Impact Goal</p>
            <p className="text-xs text-oxford/70 italic">₹25,000/month funds an entire class</p>
          </div>
          <div className="bg-dark-peach/10 px-3 py-1 rounded text-dark-peach font-bold text-xs">
            Mission 2025
          </div>
        </div>
      </div>
    </div>
  );
}